import type { SanityImageAssetDocument } from "@sanity/client";
import { createCmsClient } from "@9amhealth/cms-api";
import { Cubit } from "blac";
import { Blac } from "blac-next";
import SanityService from "src/api/SanityService";
import { globalEvents } from "src/constants/globalEvents";
import envVariables from "src/lib/envVariables";
import reportErrorSentry from "src/lib/reportErrorSentry";
import LoadingCubit, { LoadingKey } from "src/state/LoadingCubit/LoadingCubit";
import type {
  CmsCareTeamMember,
  EstablishCareStepSettings
} from "src/types/sanitySchema";

export interface SanityState<T> {
  data: T[];
  isError?: boolean;
  availableList: Set<string>;
}

type ImageAssignableFields = "resolvedImage" | "resolvedPortrait";

/**
 * Resolve any `image` / `portrait` asset refs on a care-team member and
 * assign the resulting documents back onto the member in one batched request.
 */
async function attachCareTeamImages(
  member: CmsCareTeamMember | undefined
): Promise<void> {
  if (!member) {
    return;
  }

  const assetList = SanityService.createResolveRefList();
  const bindRef = (
    ref: string | undefined,
    target: ImageAssignableFields
  ): void => {
    if (!ref) {
      return;
    }
    assetList.add(ref, (data) => {
      member[target] = data as SanityImageAssetDocument;
    });
  };

  bindRef(member.image?.asset._ref, "resolvedImage");
  bindRef(member.portrait?.asset._ref, "resolvedPortrait");

  await assetList.resolve();
}

export default class SanityCubit<T> extends Cubit<SanityState<T>> {
  // Care-team detail cache. Single-slot because callers (DoctorChat, etc.)
  // only ever view one care team at a time — no need for a keyed map yet.
  careTeamDetailsUserId: string | undefined;
  careTeamPromise: Promise<CmsCareTeamMember | undefined> | undefined;
  careTeamDetails: CmsCareTeamMember | undefined;

  // Singleton cache for "establish care" step settings — fetched once per session.
  establishCareStepSettings: EstablishCareStepSettings | undefined;
  establishCareStepPromise:
    | Promise<EstablishCareStepSettings | undefined>
    | undefined;

  constructor() {
    super({ data: [], availableList: new Set() });

    window.addEventListener(globalEvents.USER_CLEAR, () => {
      this.emit({ data: [], availableList: new Set() });
    });
  }

  get firstElement(): T | undefined {
    return this.state.data[0];
  }

  /**
   * Fetch a single content doc by `slug.current`. Uses the typed CMS API
   * (`/api/content/by-slug`) — no direct GROQ from the client.
   */
  readonly fetchBySlug = async <E = T>(
    slug: string,
    type?: string
  ): Promise<E | undefined> => {
    const loadingState = Blac.getBloc(LoadingCubit);
    loadingState.start(LoadingKey.sanityData);
    let fetchData: E | undefined = undefined;

    try {
      const cmsClient = createCmsClient(envVariables.CMS_API_BASE_URL);
      const res = await cmsClient.api.content["by-slug"].$get({
        query: type ? { slug, type } : { slug }
      });
      if (!res.ok) {
        throw new Error(`by-slug request failed: ${res.status}`);
      }
      const { doc } = (await res.json()) as { doc: T | null };

      this.emit({
        ...this.state,
        data: doc ? [doc] : []
      });

      fetchData = (doc ?? undefined) as unknown as E | undefined;
    } catch (e: unknown) {
      reportErrorSentry(e);
      this.emit({ ...this.state, isError: true });
    }

    loadingState.finish(LoadingKey.sanityData);
    return fetchData;
  };

  /**
   * Load care team members by their Sanity `_id`s via the typed CMS API.
   */
  readonly fetchCareTeamByIds = async (ids: string[]): Promise<void> => {
    if (ids.length === 0) {
      this.emit({ ...this.state, data: [] });
      return;
    }

    const loadingState = Blac.getBloc(LoadingCubit);
    loadingState.start(LoadingKey.sanityData);

    try {
      const cmsClient = createCmsClient(envVariables.CMS_API_BASE_URL);
      const res = await cmsClient.api.content["care-team"].$get({
        query: { ids: ids.join(",") }
      });
      if (!res.ok) {
        throw new Error(`care-team request failed: ${res.status}`);
      }
      const { members } = (await res.json()) as { members: T[] };
      this.emit({ ...this.state, data: members });
    } catch (e: unknown) {
      reportErrorSentry(e);
      this.emit({ ...this.state, isError: true });
    }

    loadingState.finish(LoadingKey.sanityData);
  };

  /**
   * Fetches the care team details for a given user ID, loads from sanity if not already loaded
   * @param id care team user ID
   * @returns undefined while loading and then the care team details
   */
  public readonly fetchCareTeamDetails = (
    id: string
  ): CmsCareTeamMember | undefined => {
    if (this.careTeamDetails && this.careTeamDetailsUserId === id) {
      return this.careTeamDetails;
    }

    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    if (this.careTeamPromise || !id) {
      return;
    }

    const promise = (async () => {
      if (this.state.availableList.has(id)) {
        this.state.availableList.delete(id);
        this.emit({ ...this.state });
      }

      this.careTeamDetailsUserId = id;
      let sanityData: CmsCareTeamMember | undefined;
      try {
        const cmsClient = createCmsClient(envVariables.CMS_API_BASE_URL);
        const res = await cmsClient.api.content["care-team"].$get({
          query: { userIds: id }
        });
        if (!res.ok) {
          throw new Error(`care-team request failed: ${res.status}`);
        }
        const { members } = (await res.json()) as {
          members: CmsCareTeamMember[];
        };
        sanityData = members[0];
      } catch (e) {
        reportErrorSentry(e);
      }

      await attachCareTeamImages(sanityData);

      this.careTeamDetails = sanityData ?? { _id: id, error: "No data found" };
      this.careTeamPromise = undefined;

      this.state.availableList.add(id);
      this.emit({ ...this.state });

      return sanityData;
    })();

    this.careTeamPromise = promise;
  };

  /**
   * Fetches care team members array from "Establish Care Step Settings"
   * @returns List of care team meembers
   */
  public readonly fetchEstablishCareStepSettings = ():
    | EstablishCareStepSettings
    | undefined => {
    if (this.establishCareStepSettings) {
      return this.establishCareStepSettings;
    }

    if (this.establishCareStepPromise) {
      return;
    }

    const promise = (async () => {
      let sanityData: EstablishCareStepSettings | undefined;
      try {
        const cmsClient = createCmsClient(envVariables.CMS_API_BASE_URL);
        const res =
          await cmsClient.api.content["establish-care-step-settings"].$get();
        if (!res.ok) {
          throw new Error(
            `Establish care step settings request failed: ${res.status}`
          );
        }
        sanityData = (await res.json()) as EstablishCareStepSettings;
      } catch (e) {
        reportErrorSentry(e);
      }

      if (sanityData) {
        this.establishCareStepSettings = sanityData;
      } else {
        reportErrorSentry(
          new Error(`No data found for establish care step settings`)
        );
        this.establishCareStepSettings = undefined;
      }

      this.establishCareStepPromise = undefined;

      return sanityData;
    })();

    this.establishCareStepPromise = promise;
  };
}
